//
// # PolygonEntity
//

import { graphics, Point, LineSegment } from "GenAlphaModel";
import { EntityKind } from "../entity";
import { MousePos } from "../mousepos";
import { DrawTo } from "../draw";
import { ShapeBase } from "../tree";
import { RedrawRequest } from "../redraw";

const EDGE_HIT_TOLERANCE = 5;

export class PolygonEntity extends ShapeBase<graphics.Polygon> {
  readonly entityKind = EntityKind.Polygon;

  override redraw = (to: DrawTo): unknown => {
    return to.polygon(this.data, this.style);
  };

  get vertices(): Array<PolygonVertex> {
    return this.data.points.map((pt, idx) => new PolygonVertex(this, idx, pt));
  }

  updatePoint = (index: number, loc: Point): RedrawRequest | null => {
    if (index >= this.data.points.length) return null;
    this.data.points[index] = loc;
    return this.root.requestRedraw();
  };

  // Current centroid of the polygon.
  get loc(): Point {
    return centroid(this.data.points);
  }

  // Translate all points to move the polygon to a new centroid location.
  updateLoc = (loc: Point): unknown => {
    const delta = Point.sub(loc, this.loc);
    const newPoints = this.data.points.map((pt) => Point.add(pt, delta));
    return this.update({ points: newPoints });
  };

  // Hit test against each edge of the closed polygon (for whole-shape movement).
  edgeHitTest = (mousePos: MousePos): boolean => {
    const { points } = this.data;
    if (points.length < 2) return false;
    for (let i = 0; i < points.length; i++) {
      const next = points[(i + 1) % points.length];
      const seg: LineSegment = { ends: [points[i], next] };
      if (LineSegment.closest(mousePos.sch, seg).dist < EDGE_HIT_TOLERANCE) {
        return true;
      }
    }
    return false;
  };
}

export class PolygonVertex {
  readonly entityKind = EntityKind.PolygonVertex;

  constructor(
    public parent: PolygonEntity,
    public index: number,
    public loc: Point
  ) {}

  hitTest = (mousePos: MousePos): boolean => {
    return Point.dist(mousePos.sch, this.loc) < 5;
  };

  update = (data: { loc: Point }): RedrawRequest | null => {
    this.loc = data.loc;
    return this.parent.updatePoint(this.index, data.loc);
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  highlight = (state: boolean) => this.parent.highlight(state);
}

// Compute the centroid (average) of a set of points.
function centroid(points: Array<Point>): Point {
  if (points.length === 0) return Point.origin();
  let x = 0;
  let y = 0;
  for (const pt of points) {
    x += pt.x;
    y += pt.y;
  }
  return { x: x / points.length, y: y / points.length };
}
